import * as React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import {
  CaretDown,
  DotsSixVertical,
  Funnel,
  PencilSimple,
  SortAscending,
  SortDescending,
  TrashSimple,
  CaretLeft,
  CaretRight,
  Plus,
  Function,
  Info,
} from '@phosphor-icons/react';
import { map, uniq } from 'lodash';
import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {
  FilterDropDownPillTableNew,
  getPostProcessing,
} from './FilterDropDownPillTableNew';
import Tooltip from '@/components/Tooltip';
import useEntityStore from '@/features/entity/store/useEntityStore';
import { useShallow } from 'zustand/react/shallow';
import useExploreStore from '@/features/explore/store/useExploreStore';
import useReportsStore from '@/features/reports/store/useExploreStore';
import useIframe from '@/hooks/useIframe';
import {
  getCoreRowModel,
  useReactTable,
  flexRender,
} from '@tanstack/react-table';
import TableOpsDeleteColumn from './TableOpsDeleteColumn';
import Button from '@/components/Button';
import TableStats from './TableStats';
import useGetTableColumnStats from '@/features/explore/api/getTableColumnStats';
import useGetReportsTableColumnStats from '@/features/reports/api/getTableColumnStats';
import { useTableOperation } from '@/features/explore/api/tableOperation';
import { useTableOperation as useReportsTableOperation } from '@/features/reports/api/tableOperation';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNotificationStore } from '@/stores/notifications';
import isEqual from 'lodash/isEqual';
import { Paginator } from 'primereact/paginator';
import { useLocation } from 'react-router-dom';

const TableOpsOptions = ({
  column,
  handleTableOps,
  completeData,
  checkedState,
  setCheckedState,
  tableOps,
  actualTableRowCount,
  clickedFilter,
  setClickedFilter,
  rowLength,
  schema,
  setEditColumn,
  setDeleteModalOpen,
  isTableDragging,
  msgId,
  name,
  stepId,
  headerId,
  activeColumn,
  columnIndex,
}) => {
  const [anchorEl, setAnchorEl] = React.useState();
  const [open, setOpen] = React.useState(false);
  const arrowBtnRef = React.useRef();
  const [filterOpen, setFilterOpen] = React.useState(false);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget || event.current);
    setOpen(true);
  };
  const id = `th-main-filter-${msgId}-${name}-${stepId}-${headerId}-${column}`;

  const location = useLocation();

  React.useEffect(() => {
    if (clickedFilter === column) {
      handleClick(arrowBtnRef);
    }
  }, [column, clickedFilter]);

  React.useEffect(() => {
    if (column !== activeColumn) {
      handleClose();
    }
  }, [column, activeColumn]);

  const handleClose = () => {
    setOpen(false);
    setClickedFilter();
    setFilterOpen(false);
  };

  const {
    setMessagesState,
    setRightPanelState,
    setMessagesTableState,
    rightPanelId,
    addColumnSelectedColumn,
  } = useReportsStore(
    useShallow((state) => ({
      setMessagesState: state.setMessagesState,
      setRightPanelState: state.setRightPanelState,
      setMessagesTableState: state.setMessagesTableState,
      rightPanelId: state.rightPanel.id,
      addColumnSelectedColumn: state.rightPanel.addColumnChat.selectedColumn,
    }))
  );

  const { resetToDefaultsEntity } = useEntityStore(
    useShallow((state) => ({
      resetToDefaultsEntity: state.resetToDefaults,
    }))
  );

  const DropdownButton = React.useCallback(
    ({ children, icon: Icon, onClick, disabled = false, isActive = false }) => {
      return (
        <button
          className={twMerge(
            'flex items-center gap-2 px-4 py-3 text-sm w-full text-brand-ai-200 hover:bg-pv-primary-primary-50 leading-5.5 cursor-pointer',
            twJoin(
              disabled &&
                'cursor-not-allowed hover:bg-transparent text-pv-neutral-grey-300',
              isActive && 'bg-pv-primary-primary-50'
            )
          )}
          disabled={disabled}
          {...(onClick && {
            onClick: (e) => {
              e.stopPropagation();
              onClick();
              handleClose();
            },
          })}
        >
          {Icon && (
            <Icon
              size={16}
              className={twMerge(
                'text-pv-neutral-grey-900',
                disabled && 'text-pv-neutral-grey-300'
              )}
            />
          )}
          {children}
        </button>
      );
    },
    []
  );

  const columnSchema = schema?.[column] || {};

  return (
    <>
      <button
        ref={arrowBtnRef}
        className={twMerge(
          'opacity-0 group-hover:opacity-100 hover:bg-pv-neutral-grey-50 rounded-md w-6 h-6 flex items-center justify-center',
          twJoin(
            open && 'bg-pv-neutral-grey-50 opacity-100',
            isTableDragging && 'pointer-events-none'
          )
        )}
        onClick={handleClick}
      >
        <CaretDown
          size={12}
          className={twMerge(
            'text-pv-neutral-grey-600 rotate-0 transition-transform duration-300',
            open && 'rotate-180'
          )}
        />
      </button>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        transition
        sx={{ zIndex: 5 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <div
              className="bg-white rounded-lg shadow-popup overflow-hidden mt-2"
              style={{ width: '250px' }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <div>
                  <DropdownButton
                    disabled={rowLength <= 1}
                    onClick={() => {
                      handleTableOps({
                        name: 'sort',
                        field: column,
                        type: 'ascending',
                      });
                    }}
                    icon={SortAscending}
                  >
                    Sort Ascending
                  </DropdownButton>
                  <DropdownButton
                    disabled={rowLength <= 1}
                    onClick={() => {
                      handleTableOps({
                        name: 'sort',
                        field: column,
                        type: 'descending',
                      });
                    }}
                    icon={SortDescending}
                  >
                    Sort Descending
                  </DropdownButton>
                  <hr className="border-pv-neutral-grey-100 mx-3" />
                  <DropdownButton isActive={filterOpen}>
                    <FilterDropDownPillTableNew
                      data={uniq(
                        map(completeData, column)
                          .filter((item) => item !== undefined && item !== null)
                          .flat()
                      )}
                      defaultSelectedOptions={checkedState[column]}
                      onResetClick={() => {
                        setCheckedState((prev) => {
                          const clonedArr = structuredClone(prev);
                          clonedArr[column] = [];
                          return clonedArr;
                        });
                        handleTableOps({
                          name: 'filter',
                          field: column,
                          type: null,
                        });
                      }}
                      onApply={(data) => {
                        setCheckedState((prev) => {
                          const clonedArr = structuredClone(prev);
                          clonedArr[column] = data;
                          return clonedArr;
                        });
                        handleTableOps({
                          name: 'filter',
                          field: column,
                          type: data.length > 0 ? 'equals' : null,
                          ...(data.length > 0 && { values: data }),
                        });
                        handleClose();
                      }}
                      allowEmpty={false}
                      tableOps={tableOps}
                      columnName={column}
                      actualTableRowCount={actualTableRowCount}
                      clickedFilter={clickedFilter}
                      setFilterOpen={setFilterOpen}
                      schema={schema}
                      msgId={msgId}
                      name={name}
                      stepId={stepId}
                      headerId={headerId}
                    />
                  </DropdownButton>
                  {location.pathname.includes('explore') && (
                    <>
                      <DropdownButton
                        onClick={() => {
                          setRightPanelState('id', 'addColumnChat');
                          setMessagesState('event', 'addColumn');
                          setRightPanelState(
                            'addColumnChat.orderId',
                            columnSchema.orderId
                          );
                          setRightPanelState(
                            'addColumnChat.selectedColumn',
                            columnSchema.origColName
                          );
                          resetToDefaultsEntity();
                        }}
                        icon={Plus}
                        disabled={
                          rightPanelId === 'addColumnChat' &&
                          addColumnSelectedColumn === columnSchema.origColName
                        }
                      >
                        Add Column
                      </DropdownButton>
                      <DropdownButton
                        onClick={() => setEditColumn(column)}
                        icon={PencilSimple}
                      >
                        Rename
                      </DropdownButton>
                      <DropdownButton
                        onClick={() => setDeleteModalOpen(true)}
                        icon={TrashSimple}
                      >
                        Delete
                      </DropdownButton>
                      {columnSchema?.isCalculatedColumn && (
                        <>
                          <hr className="border-pv-neutral-grey-100 mx-3" />
                          <DropdownButton
                            onClick={() =>
                              setMessagesTableState('table.calculatedColumn', {
                                showViewDetails: true,
                                name: columnSchema.label,
                                description: columnSchema.description,
                                formula: columnSchema.formula,
                                usedSources: columnSchema.usedSources,
                              })
                            }
                            icon={Info}
                          >
                            View Details
                          </DropdownButton>
                        </>
                      )}
                    </>
                  )}
                </div>
              </ClickAwayListener>
            </div>
          </Fade>
        )}
      </Popper>
    </>
  );
};

const TableOpsTableHeader = ({
  header,
  isDragging: isTableDragging,
  dragOverId,
  from,
  editColumn,
  columns,
  tableOps,
  clickedFilter,
  removeSort,
  setEditColumn,
  handleTableOps,
  completeData,
  checkedState,
  setCheckedState,
  actualTableRowCount,
  setClickedFilter,
  setActiveColumn,
  activeColumn,
  headerIndex,
  lrows,
  msg,
  setDeleteModalOpen,
  agentOperation,
  activeColumnRef,
}) => {
  const columnName = header.column.columnDef.accessorKey;
  const headerName = flexRender(
    header.column.columnDef.header,
    header.getContext()
  );

  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: header.column.id,
  });

  const style = {
    position: 'sticky',
    top: 0,
    transition: isDragging ? undefined : 'width transform 0.2s ease-in-out',
    width: header.column.getSize(),
    zIndex: isDragging ? 2 : 1,
    ...(headerIndex === 0 && { width: '1%' }),
  };

  const { addNotification } = useNotificationStore();

  const renameRef = React.useRef(null);

  const { messageEvent } = useReportsStore(
    useShallow((state) => ({
      messageEvent: state.messages.event,
    }))
  );

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!renameRef.current?.contains?.(event.target) && editColumn) {
        renameColumn();
      }
      const th = document.getElementById(
        `th-${msg.id}-${msg.displayName}-${msg.stepId}-${header.column.id}-${columnName}`
      );
      const thMainFilter = document.getElementById(
        `th-main-filter-${msg.id}-${msg.displayName}-${msg.stepId}-${header.column.id}-${columnName}`
      );
      const thNestedFilter = document.getElementById(
        `th-nested-filter-${msg.id}-${msg.displayName}-${msg.stepId}-${header.column.id}-${columnName}`
      );
      const tableStatsContainer = document.getElementById(
        `table-stats-${msg.id}-${msg.displayName}-${msg.stepId}-${columnName}`
      );
      const tableStatsPopper = document.getElementById(
        `table-stats-popper-${msg.id}-${msg.displayName}-${msg.stepId}-${columnName}`
      );
      if (
        !isTableDragging &&
        activeColumn === columnName &&
        !th?.contains?.(event.target) &&
        !thMainFilter?.contains?.(event.target) &&
        !thNestedFilter?.contains?.(event.target) &&
        !tableStatsContainer?.contains?.(event.target) &&
        !tableStatsPopper?.contains?.(event.target) &&
        messageEvent !== 'addColumn'
      ) {
        activeColumnRef.current = null;
        setActiveColumn(null);
      }
    };

    if (activeColumn === columnName) {
      if (renameRef?.current && editColumn === activeColumn) {
        renameRef.current.focus();
        moveCursorToEnd(renameRef.current);
      }
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeColumn, editColumn, headerName, messageEvent]);

  function renameColumn() {
    const newColumnName = renameRef.current.textContent.trim();
    const labels = Object.values(msg.schema).reduce((acc, column) => {
      if (column.label) {
        acc.push(column.label);
      }
      return acc;
    }, []);
    const columns = Object.keys(msg.schema).reduce((acc, column) => {
      if (column !== columnName && column !== 'petavueIndex') {
        acc.push(column);
      }
      return acc;
    }, []);
    if (
      newColumnName &&
      !labels.includes(newColumnName) &&
      newColumnName !== headerName &&
      !columns.includes(newColumnName)
    ) {
      agentOperation.mutateAsync({
        ...tableOps.current,
        operation: [
          {
            name: 'rename',
            field: columnName,
            new_name: newColumnName,
          },
        ],
      });
    } else {
      renameRef.current.textContent = headerName;
      if (newColumnName && newColumnName !== headerName) {
        addNotification({
          type: 'error',
          title: `Column with label ${newColumnName} Already Exists`,
        });
      }
    }
    setEditColumn(null);
  }

  function limitChars(element) {
    if (element.target.textContent?.length > 40) {
      element.target.textContent = element.target.textContent.substring(0, 40);
      moveCursorToEnd(element.target);
    }
  }

  function moveCursorToEnd(element) {
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  const columnSchema = msg?.schema?.[columnName] || {};

  return (
    <th
      id={`th-${msg.id}-${msg.displayName}-${msg.stepId}-${header.column.id}-${columnName}`}
      colSpan={header.colSpan}
      ref={setNodeRef}
      className={twMerge(
        'border-0.5 border-b-0 border-t-0 group text-xs text-brand-ai-1100 bg-pv-primary-primary-100',
        columnName === '#' && 'text-center cursor-default',
        headerIndex === 0 &&
          'rounded-tl-lg border-l-0 hover:bg-pv-primary-primary-100',
        headerIndex === columns.length && 'rounded-tr-lg border-r-0',
        activeColumn === columnName && 'bg-pv-primary-primary-300',
        editColumn === columnName && 'bg-white hover:bg-white z-10'
      )}
      style={style}
      {...(columnName !== '#' && {
        onClick: () => {
          activeColumnRef.current = columnName;
          setActiveColumn(columnName);
        },
      })}
    >
      <div
        className={twMerge(
          'flex gap-1 items-center focus:outline-none cursor-grab pl-1 py-2.5 pr-3',
          columnName === '#' && 'px-3',
          isTableDragging && 'cursor-grabbing',
          editColumn === columnName && 'mr-4  pr-1 cursor-default'
        )}
        style={{ height: '39.75px' }}
        {...(columnName !== '#' && !editColumn
          ? {
              ...attributes,
              ...listeners,
              onMouseDownCapture: () => {
                activeColumnRef.current = columnName;
                setActiveColumn(columnName);
              },
            }
          : {})}
      >
        {columnName !== '#' && (
          <DotsSixVertical
            size={12}
            className={twJoin(
              'text-pv-neutral-grey-600 invisible focus:outline-none cursor-grab',
              editColumn !== columnName && 'group-hover:visible',
              isTableDragging && 'cursor-grabbing'
            )}
          />
        )}
        <div
          className={twJoin(
            'items-center flex gap-2 whitespace-nowrap font-normal flex-1',
            isTableDragging && 'pointer-events-none'
          )}
          {...(editColumn === columnName && {
            style: {
              minWidth: renameRef.current.clientWidth + 78,
            },
          })}
        >
          <p
            ref={renameRef}
            contentEditable={editColumn === columnName}
            className={twMerge(
              'py-1 leading-4.5 text-xs text-brand-ai-1100 bg-transparent rounded-none focus:outline-none text-left',
              editColumn === columnName &&
                'border-b border-brand-ai-100 bg-white flex-1 w-full cursor-text',
              columnName === '#' && 'mx-auto'
            )}
            onInput={limitChars}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (e.target.textContent.length > 0) {
                  renameColumn();
                }
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const plainText = (
                e.clipboardData || window.clipboardData
              ).getData('text');
              document.execCommand('insertText', false, plainText);
            }}
            suppressContentEditableWarning
          >
            {headerName}
          </p>
          {editColumn !== columnName &&
            columnName !== '#' &&
            columnSchema?.isCalculatedColumn && (
              <div className="grid place-items-center w-6 h-6 -ml-1">
                <Function size={12} className="text-pv-neutral-grey-900" />
              </div>
            )}
          {editColumn !== columnName && columnName !== '#' && (
            <div className="flex items-center gap-0.5 w-[50px]">
              {tableOps.current.operation
                .filter((a) => a.field === columnName)
                .map((data) =>
                  data.name === 'filter' ? (
                    <Tooltip title="View filter" placement="top">
                      <button
                        className="w-6 h-6 cursor-pointer hover:bg-pv-primary-primary-50 rounded text-pv-neutral-grey-900 grid place-items-center"
                        onClick={() => {
                          setClickedFilter(columnName);
                        }}
                      >
                        <Funnel size={12} />
                      </button>
                    </Tooltip>
                  ) : data.name === 'sort' && data.type === 'ascending' ? (
                    <Tooltip title="Remove sort" placement="top">
                      <button
                        className="w-6 h-6 cursor-pointer hover:bg-pv-primary-primary-50 rounded text-pv-neutral-grey-900 grid place-items-center"
                        onClick={() => {
                          removeSort(columnName);
                        }}
                      >
                        <SortAscending size={12} />
                      </button>
                    </Tooltip>
                  ) : data.name === 'sort' && data.type === 'descending' ? (
                    <Tooltip title="Remove sort" placement="top">
                      <button
                        className="w-6 h-6 cursor-pointer hover:bg-pv-primary-primary-50 rounded text-pv-neutral-grey-900 grid place-items-center"
                        onClick={() => {
                          removeSort(columnName);
                        }}
                      >
                        <SortDescending size={12} />
                      </button>
                    </Tooltip>
                  ) : null
                )}
            </div>
          )}
        </div>
        {editColumn !== columnName && columnName !== '#' && (
          <TableOpsOptions
            column={columnName}
            handleTableOps={handleTableOps}
            completeData={completeData}
            columnIndex={headerIndex}
            checkedState={checkedState}
            setCheckedState={setCheckedState}
            tableOps={tableOps}
            actualTableRowCount={actualTableRowCount}
            clickedFilter={clickedFilter}
            setClickedFilter={setClickedFilter}
            rowLength={lrows?.length}
            from={from}
            schema={msg?.schema}
            setEditColumn={setEditColumn}
            setDeleteModalOpen={setDeleteModalOpen}
            isTableDragging={isTableDragging}
            msgId={msg.id}
            name={msg.displayName}
            stepId={msg.stepId}
            headerId={header.column.id}
            activeColumn={activeColumn}
          />
        )}
      </div>
      {isTableDragging && dragOverId === header.column.id && (
        <div
          className="absolute top-0 -right-0.5 w-0.5 h-full bg-brand-ai-100"
          style={{ zIndex: 3 }}
        />
      )}
    </th>
  );
};

const TableOpsTableCell = React.memo(
  ({
    cell,
    isDragging: isTableDragging,
    dragOverId,
    columnIndex,
    columns,
    msg,
    activeColumn,
    lrows,
    from,
    setMessageDetail,
    rowIndex,
    totalRowCount,
    itemOffset,
    curItemsPerPage,
    currentPageNo,
  }) => {
    const {
      entityOpen,
      setEntityOpen,
      selectedEntityPetavueIndex,
      selectedColumnName,
      setSelectedEntityPetavueIndex,
      setSelectedColumnName,
      setSelectedColumnLabel,
      setEntityMeta,
      entityMeta,
      setSelectedEntityRowIndex,
      setArrowClicked,
    } = useEntityStore(
      useShallow((state) => ({
        entityOpen: state.entityOpen,
        setEntityOpen: state.setEntityOpen,
        selectedEntityPetavueIndex: state.selectedEntityPetavueIndex,
        selectedColumnName: state.selectedColumnName,
        setSelectedColumnLabel: state.setSelectedColumnLabel,
        setSelectedEntityPetavueIndex: state.setSelectedEntityPetavueIndex,
        setSelectedColumnName: state.setSelectedColumnName,
        setEntityMeta: state.setEntityMeta,
        entityMeta: state.entityMeta,
        setSelectedEntityRowIndex: state.setSelectedEntityRowIndex,
        setArrowClicked: state.setArrowClicked,
      }))
    );

    const { setExploreFocusMode, metricCreating } = useExploreStore(
      useShallow((state) => ({
        setExploreFocusMode: state.setChartsFocusMode,
        metricCreating: state.searchMetric.metricCreating,
      }))
    );

    const { setRightPanelState } = useReportsStore(
      useShallow((state) => ({
        setRightPanelState: state.setRightPanelState,
      }))
    );

    const { isAppInIframe, redirectBasedOnParams } = useIframe();

    const { setNodeRef, transform } = useSortable({
      id: cell.column.id,
    });

    const style = {
      position: 'relative',
      transform: CSS.Translate.toString(transform),
      transition: 'width transform 0.2s ease-in-out',
      width: columnIndex === 0 ? '1%' : cell.column.getSize(),
    };

    const columnName = cell.column.id;
    const row = cell.row.original;

    return (
      <td
        ref={setNodeRef}
        className={twMerge(
          'px-5 py-2.5 text-brand-ai-gray-300 border-0.5 border-brand-ai-gray-400 text-xs relative whitespace-nowrap leading-4.5',
          columnIndex === 0 && 'border-l-0 px-3 text-right',
          columnIndex === columns.length && 'border-r-0',
          msg?.entityFields?.includes(columnName) &&
            !metricCreating &&
            'text-pv-primary-primary-500 cursor-pointer hover:bg-pv-primary-primary-50 hover:underline',
          selectedColumnName === columnName &&
            selectedEntityPetavueIndex === row['petavueIndex'] &&
            msg?.id === entityMeta.messageId &&
            msg?.displayName === entityMeta.outputName &&
            'text-pv-primary-primary-300 cursor-pointer bg-pv-primary-primary-50 underline',
          activeColumn === cell.column.id &&
            'bg-pv-primary-primary-50 border border-t-0 border-b-0 border-brand-ai-100',
          activeColumn === cell.column.id && rowIndex === 0 && 'border-t',
          activeColumn === cell.column.id &&
            rowIndex === lrows.length - 1 &&
            'border-b'
        )}
        style={style}
        onClick={async () => {
          if (
            Array.isArray(msg?.entityFields) &&
            msg?.entityFields.includes(columnName)
          ) {
            if (!isAppInIframe) {
              if (from === 'explore') {
                setMessageDetail();
                if (location.pathname.includes('explore')) {
                  setRightPanelState('id', null);
                } else {
                  setExploreFocusMode(true);
                }
              }
              !entityOpen && setEntityOpen(true);
              setArrowClicked(false);
              setSelectedColumnName(columnName);
              setSelectedColumnLabel(
                msg?.schema?.[columnName]?.label
                  ? msg?.schema?.[columnName].label
                  : columnName
              );
              setSelectedEntityPetavueIndex(row['petavueIndex']);
              setSelectedEntityRowIndex(
                curItemsPerPage * (currentPageNo - 1) + rowIndex
              );
              setEntityMeta({
                conversationId: msg.conversationId,
                messageId: msg.id,
                stepId: msg.stepId,
                outputName: msg.displayName,
                tableRowCount: totalRowCount,
                value: getPostProcessing(
                  row[columnName],
                  msg?.schema,
                  columnName
                ),
              });
            } else if (typeof redirectBasedOnParams === 'function') {
              try {
                const source = msg.schema?.[columnName]?.source;
                const crmObject = msg.entityDetails?.[source]?.crmObject;
                const referenceCol = msg.entityDetails?.[source]?.referenceCol;
                redirectBasedOnParams({
                  // type: 'open_in_entity_view',
                  objectName: crmObject,
                  fieldName: columnName,
                  value: row?.[columnName],
                  gsid: row?.[referenceCol],
                });
              } catch (error) {}
            }
          }
        }}
      >
        {columnName !== '#'
          ? React.isValidElement(row[columnName])
            ? row[columnName]
            : ((typeof row[columnName] === 'object' &&
                  msg.schema?.[columnName].type !== 'JSON' &&
                  msg.schema?.[columnName].format !== 'multienum') ||
                  typeof row[columnName] === 'boolean') &&
                row[columnName] !== null
              ? JSON.stringify(row[columnName])
              : getPostProcessing(row[columnName], msg?.schema, columnName)
          : `${itemOffset + (rowIndex + 1)}.`}
        {isTableDragging && dragOverId === cell.column.id && (
          <div
            className="absolute top-0 -right-0.5 w-0.5 h-full bg-brand-ai-100"
            style={{ zIndex: 3 }}
          />
        )}
      </td>
    );
  },
  (prevProps, nextProps) => {
    const { setMessageDetail: _, ...prevOtherProps } = prevProps;
    const { setMessageDetail: __, ...nextOtherProps } = nextProps;
    return isEqual(prevOtherProps, nextOtherProps);
  }
);

const TableOpsTable = React.forwardRef(
  (
    {
      rows,
      columns,
      from,
      completeData,
      localItems,
      checkedState,
      setCheckedState,
      handleTableOps,
      tableOps,
      actualTableRowCount,
      removeSort,
      msg,
      curItemsPerPage,
      setMessageDetail,
      totalRowCount,
      activeColumnRef,
      itemOffset,
      handlePageClick,
      currentPageNo,
    },
    ref
  ) => {
    const [lrows, setRows] = React.useState(rows);
    const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
    React.useEffect(() => {
      setRows(rows);
    }, [rows]);
    const [clickedFilter, setClickedFilter] = React.useState();
    const [activeColumn, setActiveColumn] = React.useState(
      activeColumnRef?.current
    );
    const [editColumn, setEditColumn] = React.useState(null);
    const {
      selectedColumnName,
      setSelectedEntityPetavueIndex,
      setEntityMeta,
      entityMeta,
      entityRowIndex,
      arrowClicked,
    } = useEntityStore(
      useShallow((state) => ({
        selectedColumnName: state.selectedColumnName,
        setSelectedEntityPetavueIndex: state.setSelectedEntityPetavueIndex,
        setEntityMeta: state.setEntityMeta,
        entityMeta: state.entityMeta,
        entityRowIndex: state.entityRowIndex,
        arrowClicked: state.arrowClicked,
      }))
    );

    const { searchMetricCreating } = useExploreStore(
      useShallow((state) => ({
        searchMetricCreating: state.searchMetric.metricCreating,
      }))
    );

    React.useEffect(() => {
      if (
        entityRowIndex !== null &&
        entityRowIndex >= 0 &&
        arrowClicked &&
        msg.displayName === entityMeta?.outputName
      ) {
        setSelectedEntityPetavueIndex(
          localItems?.[entityRowIndex]?.['petavueIndex']
        );
        setEntityMeta({
          ...entityMeta,
          value: localItems?.[entityRowIndex]?.[selectedColumnName],
        });
      }
    }, [entityRowIndex, localItems]);

    let tableColumns = ['#', ...columns].reduce((acc, column) => {
      if (column) {
        acc.push({
          id: column,
          header: msg?.schema?.[column]?.label || column,
          accessorKey: column,
        });
      }
      return acc;
    }, []);

    if (columns.length === 0) {
      tableColumns = [];
    }

    const agentOperation =
      location.pathname.includes('explore') || searchMetricCreating
        ? useReportsTableOperation({
            config: { enabled: false },
          })
        : useTableOperation({
            config: { enabled: false },
          });

    const [columnOrder, setColumnOrder] = React.useState(() =>
      tableColumns.map((c) => c.id)
    );
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragOverId, setDragOverId] = React.useState(null);
    const [currentOrder, setCurrentOrder] = React.useState(columnOrder);

    function handleDragStart() {
      setIsDragging(true);
    }

    function handleDragOver(event) {
      const { over } = event;
      if (over && columnOrder[0] === over.id) {
        setDragOverId(null);
        return;
      }
      setDragOverId(over?.id || null);
    }

    function handleDragEnd(event) {
      const { active, over } = event;
      setIsDragging(false);
      setDragOverId(null);
      if (active && over && active.id !== over.id) {
        setColumnOrder((columnOrder) => {
          const oldIndex = columnOrder.indexOf(active.id);
          const newIndex = columnOrder.indexOf(over.id);
          if (newIndex === 0) {
            return columnOrder;
          }
          return arrayMove(columnOrder, oldIndex, newIndex);
        });
        setCurrentOrder((columnOrder) => {
          const oldIndex = columnOrder.indexOf(active.id);
          const newIndex = columnOrder.indexOf(over.id);
          if (newIndex === 0) {
            return columnOrder;
          }
          return arrayMove(columnOrder, oldIndex, newIndex);
        });
        if (columnOrder.indexOf(over.id) !== 0) {
          agentOperation.mutateAsync({
            ...tableOps.current,
            operation: [
              {
                name: 'reorder',
                field: activeColumn,
                new_position: msg?.schema?.[over.id]?.orderId,
              },
            ],
          });
        }
      }
    }

    const sensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
      useSensor(TouchSensor, {
        activationConstraint: {
          delay: 250,
          tolerance: 8,
        },
      }),
      useSensor(KeyboardSensor, {})
    );

    const table = useReactTable({
      data: lrows,
      columns: tableColumns,
      getCoreRowModel: getCoreRowModel(),
      state: {
        columnOrder: currentOrder,
      },
      onColumnOrderChange: setCurrentOrder,
    });

    const tableColumnStatsFunc = location.pathname.includes('explore')
      ? useGetReportsTableColumnStats
      : useGetTableColumnStats;

    const tableColumnStats = tableColumnStatsFunc({
      config: {
        enabled: Boolean(activeColumn),
      },
      payload: {
        conversationId: msg.conversationId,
        messageId: msg.id,
        stepId: msg.stepId,
        outputName: msg.displayName,
        columnName: activeColumn,
      },
    });

    return (
      <>
        <div
          className="overflow-auto scrollbar-hide-y"
          style={{ maxHeight: '450px' }}
        >
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    <SortableContext
                      items={currentOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header, headerIndex) => {
                        return (
                          <TableOpsTableHeader
                            key={header.id}
                            header={header}
                            isDragging={isDragging}
                            dragOverId={dragOverId}
                            from={from}
                            editColumn={editColumn}
                            columns={columns}
                            tableOps={tableOps}
                            clickedFilter={clickedFilter}
                            removeSort={removeSort}
                            setEditColumn={setEditColumn}
                            handleTableOps={handleTableOps}
                            completeData={completeData}
                            checkedState={checkedState}
                            setCheckedState={setCheckedState}
                            actualTableRowCount={actualTableRowCount}
                            setClickedFilter={setClickedFilter}
                            setActiveColumn={setActiveColumn}
                            activeColumn={activeColumn}
                            headerIndex={headerIndex}
                            lrows={lrows}
                            msg={msg}
                            setDeleteModalOpen={setDeleteModalOpen}
                            agentOperation={agentOperation}
                            activeColumnRef={activeColumnRef}
                          />
                        );
                      })}
                    </SortableContext>
                  </tr>
                ))}
              </thead>
              <tbody
                className={twJoin(
                  'bg-white divide-y divide-gray-200',
                  actualTableRowCount > 0 &&
                    lrows.length === 0 &&
                    'h-20 w-full border-b border-brand-ai-gray-400'
                )}
              >
                {table.getRowModel().rows.map((tempRow, rowIndex) => {
                  return (
                    <tr key={tempRow.id} id={`table-row-${tempRow.id}`}>
                      {tempRow.getVisibleCells().map((cell, columnIndex) => {
                        return (
                          <TableOpsTableCell
                            key={cell.id}
                            cell={cell}
                            isDragging={isDragging}
                            dragOverId={dragOverId}
                            columnIndex={columnIndex}
                            columns={columns}
                            msg={msg}
                            activeColumn={activeColumn}
                            lrows={lrows}
                            from={from}
                            setMessageDetail={setMessageDetail}
                            rowIndex={rowIndex}
                            totalRowCount={totalRowCount}
                            itemOffset={itemOffset}
                            curItemsPerPage={curItemsPerPage}
                            currentPageNo={currentPageNo}
                          />
                        );
                      })}
                    </tr>
                  );
                })}
                {actualTableRowCount > 0 && lrows.length === 0 && (
                  <div className="py-3.5 grid gap-2 absolute transform -translate-x-1/2 left-1/2">
                    <p className="text-brand-ai-gray-300 text-xs text-center">
                      No data found for the selected filter.
                    </p>
                    <Button
                      variant="secondary"
                      size="small"
                      className={{
                        button: {
                          root: 'w-fit mx-auto',
                        },
                      }}
                      onClick={() => {
                        //TODO: temp clear filter
                      }}
                    >
                      Clear Filter
                    </Button>
                  </div>
                )}
              </tbody>
            </table>
          </DndContext>
        </div>
        <div className="h-10 p-1.5 flex items-center justify-between">
          <Paginator
            first={itemOffset}
            rows={curItemsPerPage}
            totalRecords={totalRowCount}
            onPageChange={handlePageClick}
            pt={{ root: 'p-0' }}
            template={{
              layout: 'PrevPageLink CurrentPageReport NextPageLink',
              PrevPageLink: (options) => {
                return (
                  <Button
                    variant="ghost-blue"
                    onClick={options.onClick}
                    disabled={options.disabled}
                  >
                    <CaretLeft size={12} className="text-inherit" />
                  </Button>
                );
              },
              CurrentPageReport: (options) => {
                return (
                  <span className="text-[10px] leading-[16px] text-pv-text-secondary-text mx-1 select-none">
                    {options.first} - {options.last} of {options.totalRecords}
                  </span>
                );
              },
              NextPageLink: (options) => {
                return (
                  <Button
                    variant="ghost-blue"
                    onClick={options.onClick}
                    disabled={options.disabled}
                  >
                    <CaretRight size={12} className="text-inherit" />
                  </Button>
                );
              },
            }}
          />
          <TableStats
            schema={msg?.schema}
            totalRowCount={totalRowCount}
            activeColumn={activeColumn}
            tableColumnStats={tableColumnStats}
            msgId={msg.id}
            name={msg.displayName}
            stepId={msg.stepId}
          />
        </div>
        <TableOpsDeleteColumn
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          columnName={activeColumn}
          tableOps={tableOps}
        />
      </>
    );
  }
);

const areEqual = (prevProps, nextProps) => {
  return (
    isEqual(prevProps.rows, nextProps.rows) &&
    isEqual(prevProps.columns, nextProps.columns) &&
    isEqual(prevProps.handleTableOps, nextProps.handleTableOps) &&
    isEqual(prevProps.msg, nextProps.msg) &&
    isEqual(prevProps.itemOffset, nextProps.itemOffset) &&
    isEqual(prevProps.handlePageClick, nextProps.handlePageClick)
  );
};

export default React.memo(TableOpsTable, areEqual);
